import type { DeliveryLog, DeliveryStatus } from '@/types/delivery';

export type ViewFilter = 'all' | 'messages';

const STATUS_LABEL: Record<DeliveryStatus, string> = {
  delivered: 'Delivered',
  no_response: 'No response',
  rejected: 'Rejected',
  not_delivered: 'Not delivered',
  skipped: 'Skipped',
};

export const STATUS_LABEL_BY_TEXT: Record<string, string> = {
  'Delivered to your app': 'Delivered',
  "Your app didn't reply in time": 'No response',
  "Your app couldn't process it": 'Rejected',
  'CLI / Webhook not responding': 'Not delivered',
  'Destination offline': 'Not delivered',
  'No sandbox destination': 'Not delivered',
  'Messaging is paused': 'Paused',
  'Outside sandbox flow': 'Skipped',
  "We didn't deliver this.": 'Skipped',
};

export const STATUS_TOOLTIP_BY_TEXT: Record<string, string | null> = {
  'Delivered to your app': null,
  "Your app didn't reply in time": null,
  "Your app couldn't process it": null,
  'CLI / Webhook not responding':
    "We got this message but couldn't deliver it because no destination was responding. Start the HookMyApp CLI to receive messages, or set a custom webhook URL in channel settings. Future messages will be delivered once one is running.",
  'Destination offline':
    "We tried to deliver this but your destination wasn't reachable. The HookMyApp CLI tunnel may be offline. Restart it, or check your custom webhook URL in channel settings.",
  'No sandbox destination': 'No destination is connected to this sandbox session.',
  'Messaging is paused':
    'Messaging is paused for this channel. Enable it on the channel page to resume sending and receiving.',
  'Outside sandbox flow': "The sender wasn't part of an active sandbox flow.",
  "We didn't deliver this.": null,
};

export function statusLabelFor(log: DeliveryLog): string {
  const fallback = STATUS_LABEL[log.hookmyapp.status];
  return STATUS_LABEL_BY_TEXT[log.hookmyapp.statusText] ?? fallback;
}

export function statusTooltipFor(log: DeliveryLog): string | null {
  return STATUS_TOOLTIP_BY_TEXT[log.hookmyapp.statusText] ?? null;
}

/** AIT-549: event types a subscription row counts as a "message". */
const MESSAGE_EVENT_TYPES = new Set(['whatsapp.message.received', 'instagram.message.received']);

interface EventEnvelopeView {
  id: string;
  type: string;
  data: unknown;
}

/** AIT-549: the normalized envelope an event-subscription row carries as its body, or null. */
function envelopeOf(body: unknown): EventEnvelopeView | null {
  const parsed = normalizeJson(body);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const e = parsed as { id?: unknown; type?: unknown; data?: unknown };
  return typeof e.id === 'string' && e.id.startsWith('evt_') && typeof e.type === 'string'
    ? { id: e.id, type: e.type, data: e.data }
    : null;
}

/** AIT-549: the label shown for a subscription row, else null. */
export function destinationLabelFor(log: DeliveryLog): string | null {
  const d = log.hookmyapp.destination;
  return d?.type === 'subscription' ? d.label ?? 'Event subscription' : null;
}

function destinationSearchText(log: DeliveryLog): string {
  const d = log.hookmyapp.destination;
  if (!d) return '';
  if (d.type === 'webhook') return d.url;
  if (d.type === 'cli') return d.label;
  return `${destinationLabelFor(log)} ${d.url}`;
}

export function deliverySearchText(log: DeliveryLog): string {
  return [
    statusLabelFor(log),
    log.hookmyapp.statusText,
    log.sender ?? '',
    log.messageId ?? '',
    destinationSearchText(log),
    stringifyForSearch(log.meta),
    stringifyForSearch(log.hookmyapp.appResponse.body),
  ]
    .join(' ')
    .toLowerCase();
}

/**
 * Pull the message-bearing array out of a delivery body. Handles WhatsApp
 * Cloud API (`entry[].changes[].value.messages[]`), Instagram Messaging
 * (`entry[].messaging[]`) and, for event-subscription rows (AIT-549), the
 * normalized envelope: its `data` for the two received-message types.
 */
export function extractMessageView(
  body: unknown,
  filter: ViewFilter,
): unknown[] | null {
  if (filter === 'all' || body == null) return null;
  const parsed = normalizeJson(body);
  if (!parsed || typeof parsed !== 'object') return null;
  const envelope = envelopeOf(parsed);
  if (envelope) {
    return filter === 'messages' && MESSAGE_EVENT_TYPES.has(envelope.type) ? [envelope.data] : null;
  }
  const root = parsed as {
    entry?: Array<{
      changes?: Array<{ value?: Record<string, unknown> }>;
      messaging?: unknown[];
    }>;
  };
  const out: unknown[] = [];
  for (const entry of root.entry ?? []) {
    for (const change of entry.changes ?? []) {
      const arr = change.value?.[filter];
      if (Array.isArray(arr)) out.push(...arr);
    }
    if (filter === 'messages' && Array.isArray(entry.messaging)) {
      out.push(...entry.messaging);
    }
  }
  return out.length ? out : null;
}

/**
 * The Messages filter. AIT-184: a primary row with a messageId is a real
 * message even when undelivered. AIT-549: a subscription row counts only when
 * its envelope is a received message (status and comment rows carry a
 * messageId too, but are not messages).
 */
export function isMessageDelivery(log: DeliveryLog): boolean {
  const view = Boolean(extractMessageView(log.meta, 'messages'));
  return log.hookmyapp.destination?.type === 'subscription' ? view : view || log.messageId != null;
}

/**
 * Key for the "related deliveries" panel. Primary rows and subscription
 * message rows group by messageId (the primary and every subscription attempt
 * of one inbound message); subscription status/comment rows group by their
 * event id (the attempts of that one event).
 */
export function relatedKeyFor(log: DeliveryLog): string | null {
  if (log.hookmyapp.destination?.type === 'subscription') {
    const envelope = envelopeOf(log.meta);
    if (envelope && !MESSAGE_EVENT_TYPES.has(envelope.type)) return `event:${envelope.id}`;
  }
  return log.messageId;
}

export function normalizeJson(value: unknown): unknown | undefined {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (trimmed[0] !== '{' && trimmed[0] !== '[') return undefined;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return undefined;
  }
}

export function stringifyBody(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

function stringifyForSearch(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}
